import "./CMSStatus.scss";

import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import { Checkbox, DatePicker, TextArea, TextInput } from "components/inputs";
import LoadingIcon from "components/Loading/LoadingIcon";
import KaboodleModal from "components/Modals/KaboodleModal";
import { OCRStatusList } from "components/QuerySection/components/OCRStatusList";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useDeleteCMSDevelopmentMutation,
  useGetMSADecisionsQuery,
  useGetMSAReReviewsQuery,
  usePatchCMSDevelopmentMutation,
  usePatchMSAByIdMutation,
} from "services/msa-api";
import type {
  MSADataType,
  MSADecisionType,
  MSADevelopmentType,
  MSAReReviewType,
} from "types/DataTypes";
import { STRING_TO_DATE } from "utils/date-utils";

import MSAReReview from "../../msa/MSAReReview";
import CMSSavingsTracker from "../cms-submission/CMSSavingsTracker";
import MSADecisionStatus from "./cms-decision/CMSDecision";
import MSADecisionOCRReview from "./MSADecisionOCRReview";
import MSADevelopmentOCRReview from "./MSADevelopmentOCRReview";

type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};
type CMSStatusProps = {
  msa: MSADataType;
};

const useBlurSaveText = (
  serverValue: string,
  onSave: (value: string) => Promise<void>,
) => {
  const [value, setValue] = useState(serverValue);
  const latestValueRef = useRef(serverValue);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) return;
    setValue(serverValue);
    latestValueRef.current = serverValue;
  }, [serverValue]);

  return {
    value,
    setText: (newValue: string) => {
      setValue(newValue);
      latestValueRef.current = newValue;
    },
    onFocus: () => {
      isFocusedRef.current = true;
    },
    onBlur: () => {
      isFocusedRef.current = false;
      if (latestValueRef.current !== serverValue) {
        onSave(latestValueRef.current);
      }
    },
  };
};

type DevelopmentDocumentsRequestedProps = {
  developmentId: number;
  documentsRequested: string;
  onSave: (value: string) => Promise<void>;
};

const DevelopmentDocumentsRequested = ({
  developmentId,
  documentsRequested,
  onSave,
}: DevelopmentDocumentsRequestedProps) => {
  const { value, setText, onFocus, onBlur } = useBlurSaveText(
    documentsRequested,
    onSave,
  );

  return (
    <TextArea
      name={`development-${developmentId}-documents-requested`}
      classNames="documents-requested-textarea"
      label=""
      value={value}
      onChange={(e) => setText(typeof e === "string" ? e : e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

type DevelopmentNotesProps = {
  developmentId: number;
  notes: string;
  onSave: (value: string) => Promise<void>;
};

const DevelopmentNotes = ({
  developmentId,
  notes,
  onSave,
}: DevelopmentNotesProps) => {
  const { value, setText, onFocus, onBlur } = useBlurSaveText(notes, onSave);

  return (
    <div onFocus={onFocus} onBlur={onBlur}>
      <TextInput
        name={`development-${developmentId}-notes`}
        value={value}
        maxLength={255}
        onChange={(e) => setText(e.target.value)}
      />
    </div>
  );
};

const CMSStatus = ({ msa }: CMSStatusProps) => {
  const [patchMsaById] = usePatchMSAByIdMutation();
  const [patchCMSDevelopment] = usePatchCMSDevelopmentMutation();
  const [deleteCMSDevelopment, { isLoading: isDeleting }] =
    useDeleteCMSDevelopmentMutation();
  const [developmentToDelete, setDevelopmentToDelete] = useState<number | null>(
    null,
  );
  // fetch decisions and re-reviews data
  const { data: decisions, isLoading: decisionsLoading } =
    useGetMSADecisionsQuery({
      msaId: msa.id.toString(),
    });
  const { data: reReviews, isLoading: reReviewsLoading } =
    useGetMSAReReviewsQuery({
      msaId: msa.id.toString(),
    });

  const sortDevelopmentsByDate = (developments: MSADevelopmentType[]) => {
    return [...developments].sort((a, b) => {
      const dateDiff =
        new Date(b.created).getTime() - new Date(a.created).getTime();
      if (!Number.isNaN(dateDiff) && dateDiff !== 0) {
        return dateDiff;
      }
      return b.id - a.id;
    });
  };

  const sortedDevelopments = useMemo(
    () => (msa.developments ? sortDevelopmentsByDate(msa.developments) : []),
    [msa.developments],
  );

  const handleSave = async (field: string, value: unknown) => {
    try {
      if (field.startsWith("developments.")) {
        const [, developmentId, fieldName] = field.split(".");
        await patchCMSDevelopment({
          developmentId,
          data: { [fieldName]: value },
        }).unwrap();
      } else {
        await patchMsaById({
          msaId: msa.id.toString(),
          data: { [field]: value },
        }).unwrap();
      }
    } catch (error) {
      KaboodleToaster.error("Failed to update field");
    }
  };

  const handleDelete = async () => {
    if (!developmentToDelete) return;

    try {
      await deleteCMSDevelopment({
        developmentId: developmentToDelete.toString(),
      }).unwrap();

      setDevelopmentToDelete(null);
      KaboodleToaster.success("Development deleted successfully");
    } catch (error) {
      KaboodleToaster.error(
        error?.data?.detail || "Failed to delete development",
      );
    }
  };

  // Prepare alternating items for rendering
  // TODO: use the foreign keys to make sure these are in the correct order
  const renderAlternatingItems = () => {
    // Extract the actual arrays from the paginated response
    const decisionsArray =
      (decisions as unknown as PaginatedResponse<MSADecisionType>)?.results ||
      [];
    const reReviewsArray =
      (reReviews as unknown as PaginatedResponse<MSAReReviewType>)?.results ||
      [];

    const items = [];
    const maxCount = Math.max(decisionsArray.length, reReviewsArray.length);

    for (let i = 0; i < maxCount; i++) {
      // Add decision if available
      if (i < decisionsArray.length) {
        items.push(
          <MSADecisionStatus
            // Includes `modified` so this remounts and re-seeds its local state when the decision changes externally, e.g. the OCR review modal's save.
            key={`decision-${decisionsArray[i].id}-${decisionsArray[i].modified}`}
            msaId={msa.id.toString()}
            decision={decisionsArray[i]}
          />,
        );
      }

      // Add re-review if available
      if (i < reReviewsArray.length) {
        items.push(
          <MSAReReview
            key={`review-${reReviewsArray[i].id}`}
            review={reReviewsArray[i]}
          />,
        );
      }
    }
    return items;
  };

  // Show loading state if data is being fetched
  if (decisionsLoading || reReviewsLoading) {
    return <LoadingIcon />;
  }

  const alternatingItems = renderAlternatingItems();

  return (
    <div className="swim-column">
      {/* msa_decision and msa_development are excluded since the two OCR review widgets below own those jobs' status end to end. */}
      <OCRStatusList
        fileId={msa.file.id}
        excludeJobTypes={["msa_decision", "msa_development"]}
      />
      <MSADecisionOCRReview msaId={msa.id.toString()} fileId={msa.file.id} />
      <MSADevelopmentOCRReview msaId={msa.id.toString()} fileId={msa.file.id} />

      <KaboodleModal
        isOpen={!!developmentToDelete}
        onClose={() => setDevelopmentToDelete(null)}
        footer={
          <div className="flex jc-c">
            <Button
              className="delete"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Confirm Delete
            </Button>
            <Button onClick={() => setDevelopmentToDelete(null)}>Cancel</Button>
          </div>
        }
        children={
          <Card heading="Delete Development">
            <p>Are you sure you want to delete this development?</p>
          </Card>
        }
      />
      {sortedDevelopments.length > 0 ? (
        sortedDevelopments?.map((development) => (
          <div
            key={development.id}
            className="data-block max-content-w development-card"
          >
            <div className="data-block-header flex jc-sb">
              Development
              <div className="flex jc-end">
                <ButtonIcon
                  size="small"
                  onClick={() => setDevelopmentToDelete(development.id)}
                  title="Delete Development"
                >
                  delete
                </ButtonIcon>
              </div>
            </div>

            <div className="data-values">
              <label>Status:</label>
              <figure
                className={`txt-color ${development.status_display === "Complete" ? "green" : "red"}`}
              >
                {development.status_display}
              </figure>

              <label>Date Uploaded:</label>
              <figure>{development.created}</figure>

              <label>Date Received:</label>
              <DatePicker
                name={`development-${development.id}-date-received`}
                onDateChange={(date) =>
                  handleSave(
                    `developments.${development.id}.date_received`,
                    format(date, "yyyy-MM-dd"),
                  )
                }
                initialDate={
                  development.date_received
                    ? STRING_TO_DATE(development.date_received.split(" ")[0])
                    : undefined
                }
              />

              <label>Date Sent to Client:</label>
              <DatePicker
                name={`development-${development.id}-date-sent`}
                onDateChange={(date) =>
                  handleSave(
                    `developments.${development.id}.date_sent_to_client`,
                    format(date, "yyyy-MM-dd"),
                  )
                }
                initialDate={
                  development.date_sent_to_client
                    ? STRING_TO_DATE(
                        development.date_sent_to_client.split(" ")[0],
                      )
                    : undefined
                }
              />

              <label>Date Materials Received From Client:</label>
              <DatePicker
                name={`development-${development.id}-date-received-from-client`}
                onDateChange={(date) =>
                  handleSave(
                    `developments.${development.id}.date_received_from_client`,
                    format(date, "yyyy-MM-dd"),
                  )
                }
                initialDate={
                  development.date_received_from_client
                    ? STRING_TO_DATE(
                        development.date_received_from_client.split(" ")[0],
                      )
                    : undefined
                }
              />

              <label>Documents Requested:</label>
              <DevelopmentDocumentsRequested
                developmentId={development.id}
                documentsRequested={development.documents_requested || ""}
                onSave={(value) =>
                  handleSave(
                    `developments.${development.id}.documents_requested`,
                    value,
                  )
                }
              />

              <label>Notes:</label>
              <DevelopmentNotes
                developmentId={development.id}
                notes={development.notes || ""}
                onSave={(value) =>
                  handleSave(`developments.${development.id}.notes`, value)
                }
              />

              <label>Closeout:</label>
              <Checkbox
                name={`development-${development.id}-is-closeout`}
                label=""
                checked={development.is_closeout}
                onChange={(event) =>
                  handleSave(
                    `developments.${development.id}.is_closeout`,
                    event.target.checked,
                  )
                }
              />

              <label>Date Materials Submitted:</label>
              <DatePicker
                name={`development-${development.id}-date-acknowledged`}
                onDateChange={(date) =>
                  handleSave(
                    `developments.${development.id}.date_acknowledged`,
                    format(date, "yyyy-MM-dd"),
                  )
                }
                initialDate={
                  development.date_acknowledged
                    ? STRING_TO_DATE(
                        development.date_acknowledged.split(" ")[0],
                      )
                    : undefined
                }
              />
            </div>
          </div>
        ))
      ) : (
        <div className="data-block max-content-w">
          <div className="data-block-header">Development</div>
          <div className="data-values">
            <p className="text-center">
              No development letters have been recorded yet. Click the "Add
              Development" button to the right to record the first development.
            </p>
          </div>
        </div>
      )}

      {alternatingItems}
      {(!(decisions as unknown as PaginatedResponse<MSADecisionType>)
        ?.results ||
        (decisions as unknown as PaginatedResponse<MSADecisionType>).results
          .length === 0) &&
        (!(reReviews as unknown as PaginatedResponse<MSAReReviewType>)
          ?.results ||
          (reReviews as unknown as PaginatedResponse<MSAReReviewType>).results
            .length === 0) && (
          <>
            <div className="data-block max-content-w">
              <div className="data-block-header">CMS Decisions</div>
              <div className="data-values">
                <p className="text-center">No decisions currently exist.</p>
              </div>
            </div>
            <div className="data-block max-content-w">
              <div className="data-block-header">Re-Reviews</div>
              <div className="data-values">
                <p className="text-center">No re-reviews currently exist.</p>
              </div>
            </div>
          </>
        )}

      <CMSSavingsTracker service={msa} />
    </div>
  );
};

export default CMSStatus;
